/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";

export default function data(
  members = [],
  onAssignRoleClick = () => {},
  onToggleStatusClick = () => {},
  statusUpdatingMemberId = null
) {
  const Member = ({ name, email }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="button" fontWeight="medium">
        {name}
      </MDTypography>
      <MDTypography variant="caption" color="text">
        {email}
      </MDTypography>
    </MDBox>
  );

  const getName = (item) => item.name || item.Name || "";
  const getEmail = (item) => item.email || item.Email || "";
  const getUserName = (item) => item.userName || item.UserName || "";
  const getPhoneNumber = (item) => item.phoneNumber || item.PhoneNumber || "";
  const getBillingAddress = (item) => item.billingAddress || item.BillingAddress || "";
  const getMemberId = (item) =>
    item.memberId ?? item.MemberId ?? item.memberID ?? item.MemberID ?? item.id ?? item.Id ?? null;
  const getIsActive = (item) => item.isActive ?? item.IsActive ?? false;
  const getIsAdministrativeUser = (item) => {
    const value = item?.isAdministrativeUser ?? item?.IsAdministrativeUser;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "yes";
    }
    return false;
  };
  const palette = ["info", "success", "warning", "error", "dark", "primary"];

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const getRoleNames = (item) => {
    const directRoleNames = normalizeArray(item.roleNames ?? item.RoleNames);
    if (directRoleNames.length > 0) {
      return directRoleNames.filter((role) => typeof role === "string" && role.trim() !== "");
    }

    const memberRoles = normalizeArray(item.memberRoles ?? item.MemberRoles);
    return memberRoles
      .map((mr) => mr?.roleName ?? mr?.RoleName ?? mr?.role?.name ?? mr?.Role?.Name)
      .filter((role) => typeof role === "string" && role.trim() !== "");
  };

  const getMemberRoles = (item) => {
    const memberRoles = normalizeArray(item.memberRoles ?? item.MemberRoles);
    return memberRoles
      .map((mr) => ({
        memberRoleId: mr?.memberRoleId ?? mr?.MemberRoleId ?? mr?.id ?? mr?.Id ?? null,
        roleId: mr?.roleId ?? mr?.RoleId ?? mr?.role?.roleId ?? mr?.Role?.RoleId ?? null,
        roleName: mr?.roleName ?? mr?.RoleName ?? mr?.role?.name ?? mr?.Role?.Name ?? "",
        isActive: mr?.isActive ?? mr?.IsActive ?? true,
      }))
      .filter((mr) => typeof mr.roleName === "string" && mr.roleName.trim() !== "");
  };

  const RoleBadges = ({ roleNames }) => (
    <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={0.5}>
      {roleNames.length > 0 ? (
        roleNames.map((role, index) => (
          <MDBox
            key={`${role}-${index}`}
            display="inline-block"
            px={1.2}
            py={0.4}
            borderRadius="lg"
            bgColor={palette[index % palette.length]}
          >
            <MDTypography variant="caption" color="white" fontWeight="bold">
              {role}
            </MDTypography>
          </MDBox>
        ))
      ) : (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          -
        </MDTypography>
      )}
    </MDBox>
  );

  return {
    columns: [
      {
        Header: "member",
        accessor: "memberSearch",
        width: "25%",
        align: "left",
        Cell: ({ row }) => <Member name={row.original.name} email={row.original.email} />,
      },
      {
        Header: "username",
        accessor: "userName",
        align: "left",
        Cell: ({ value }) => (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {value}
          </MDTypography>
        ),
      },
      {
        Header: "phone",
        accessor: "phoneNumber",
        align: "center",
        Cell: ({ value }) => (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {value || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "billing address",
        accessor: "billingAddress",
        align: "left",
        Cell: ({ value }) => (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {value || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "admin user",
        accessor: "isAdministrativeUserText",
        align: "center",
        Cell: ({ row }) => row.original.isAdministrativeUserBadge,
      },
      {
        Header: "roles",
        accessor: "rolesText",
        align: "center",
        Cell: ({ row }) => <RoleBadges roleNames={row.original.roleNames} />,
      },
      {
        Header: "status",
        accessor: "statusText",
        align: "center",
        Cell: ({ row }) => <StatusBadge isActive={row.original.isActive} />,
      },
      {
        Header: "action",
        accessor: "actionText",
        align: "center",
        Cell: ({ row }) => (
          <MDBox display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <Tooltip title="Assign Roles" placement="top">
              <span>
                <IconButton
                  color="info"
                  size="small"
                  onClick={() => onAssignRoleClick(row.original)}
                >
                  <Icon fontSize="small">manage_accounts</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={row.original.isActive ? "Deactivate User" : "Activate User"}
              placement="top"
            >
              <span>
                <IconButton
                  color={row.original.isActive ? "error" : "success"}
                  size="small"
                  onClick={() => onToggleStatusClick(row.original)}
                  disabled={String(statusUpdatingMemberId) === String(row.original.memberId)}
                >
                  <Icon fontSize="small">
                    {row.original.isActive ? "person_off" : "how_to_reg"}
                  </Icon>
                </IconButton>
              </span>
            </Tooltip>
          </MDBox>
        ),
      },
    ],
    rows: members.map((item) => ({
      memberId: getMemberId(item),
      name: getName(item),
      email: getEmail(item),
      userName: getUserName(item),
      phoneNumber: getPhoneNumber(item),
      billingAddress: getBillingAddress(item),
      isAdministrativeUser: getIsAdministrativeUser(item),
      isAdministrativeUserText: getIsAdministrativeUser(item) ? "Yes" : "No",
      isAdministrativeUserBadge: (
        <MDBadge
          badgeContent={getIsAdministrativeUser(item) ? "Yes" : "No"}
          color={getIsAdministrativeUser(item) ? "info" : "secondary"}
          variant="gradient"
          size="sm"
        />
      ),
      memberRoles: getMemberRoles(item),
      roleNames: getRoleNames(item),
      rolesText: getRoleNames(item).join(" "),
      isActive: getIsActive(item),
      statusText: getIsActive(item) ? "Active" : "Deactivated",
      actionText: "Assign Role",
      memberSearch: `${getName(item)} ${getEmail(item)} ${getUserName(item)} ${getPhoneNumber(
        item
      )} ${getBillingAddress(item)} ${getRoleNames(item).join(" ")} ${
        getIsAdministrativeUser(item) ? "admin yes" : "admin no"
      }`.trim(),
    })),
  };
}
const StatusBadge = ({ isActive }) => (
  <MDBox
    display="inline-block"
    px={1.5}
    py={0.5}
    borderRadius="lg"
    bgColor={isActive ? "success" : "error"}
  >
    <MDTypography variant="caption" color="white" fontWeight="bold">
      {isActive ? "Active" : "Deactivated"}
    </MDTypography>
  </MDBox>
);
